import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faSortUp, faSortDown, faSort, faList, faFileDownload } from '@fortawesome/free-solid-svg-icons'
import { Box, Tooltip, IconButton, Button, Typography, CircularProgress } from '@mui/material'
import Alert from '@mui/material/Alert'
import { ResponsiveTable, TableEmptyState } from '../TableComponents'
import { formatDate, formatMinutes } from '../../services/format'
import ExpandableText from '../ExpandableText'
import styles from './CallResultsTable.module.scss'

const CallResultsTable = ({
  calls = [],
  loading = false,
  searched = false,
  error = null,
  page = 1,
  totalPages = 1,
  totalCalls = 0,
  sortField = 'call_date',
  sortDirection = 'desc',
  onPageChange,
  onSort,
  onResetSorting,
  onRowClick,
  onExportCsv,
  exporting = false
}) => {
  const { t } = useTranslation()
  const tableTopRef = useRef(null)

  const renderSortIcon = (columnId) => {
    const isActive = sortField === columnId
    const icon = isActive 
      ? (sortDirection === 'asc' ? faSortUp : faSortDown)
      : faSort
    
    return (
      <FontAwesomeIcon 
        icon={icon} 
        className={styles.sortIcon}
        style={{ 
          opacity: isActive ? 1 : 0.3,
          fontSize: '0.8em'
        }}
      />
    )
  }

  const columns = [
    {
      id: 'details',
      label: '',
      headerCellProps: { 'aria-label': 'Actions' },
      align: 'center',
      cellClassName: styles.customTableCell,
      renderCell: (row) => (
        <Tooltip title={t('calls.viewDetails')}>
          <IconButton
            className={styles.detailsButton}
            size='small'
            onClick={(e) => {
              e.stopPropagation()
              onRowClick(row)
            }}
          >
            <FontAwesomeIcon icon={faEye} size='sm' />
          </IconButton>
        </Tooltip>
      )
    },
    {
      id: 'call_id',
      label: 'ID',
      headerCellProps: { title: 'Call ID' },
      sortable: false,
      cellClassName: styles.customTableCell,
      style: { maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
    },
    {
      id: 'call_date',
      label: 'Date',
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('call_date'),
      renderCell: (row) => (row.call_date ? formatDate(new Date(row.call_date), 'dateTime') : t('common.na'))
    },
    {
      id: 'campaign_name',
      label: 'Campaign',
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('campaign_name'),
      renderCell: (row) => (row.campaign_name || t('calls.unknown'))
    },
    {
      id: 'direction',
      label: 'Dir.',
      headerCellProps: { title: 'Direction' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('direction'),
      renderCell: (row) => (row.direction || t('calls.unknown'))
    },
    {
      id: 'phone_number',
      label: 'Phone',
      headerCellProps: { title: 'Phone Number' },
      sortable: false,
      cellClassName: styles.customTableCell,
      renderCell: (row) => {
        const phoneNumber = row.direction === 'Inbound'
          ? (row.caller_number || row.dnis)
          : (row.dnis || row.caller_number)
        return phoneNumber || '—'
      }
    },
    {
      id: 'caller_name',
      label: 'Name',
      headerCellProps: { title: 'Customer Name' },
      sortable: false,
      cellClassName: styles.customTableCell,
      renderCell: (row) => (row.caller_name || '—')
    },
    {
      id: 'caller_company',
      label: 'Company',
      headerCellProps: { title: 'Customer Company' },
      sortable: false,
      cellClassName: styles.customTableCell,
      columnClassName: styles.hideBelowXl,
      renderCell: (row) => (row.caller_company || '—')
    },
    {
      id: 'disposition',
      label: 'Type',
      headerCellProps: { title: 'Call Type / Disposition' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('disposition'),
      renderCell: (row) => (row.disposition || t('calls.unknown'))
    },
    {
      id: 'agent_name',
      label: 'Agent',
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('agent_name'),
      renderCell: (row) => (row.agent_name || t('calls.unknown'))
    },
    {
      id: 'caller_email',
      label: 'Email',
      sortable: true,
      cellClassName: styles.customTableCell,
      columnClassName: styles.hideBelowXl,
      style: { maxWidth: '200px', wordBreak: 'break-word' },
      renderSortIcon: () => renderSortIcon('caller_email'),
      renderCell: (row) => (row.caller_email || '—')
    },
    {
      id: 'ring_time',
      label: 'Ring',
      headerCellProps: { title: 'Ring Time' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('ring_time'),
      renderCell: (row) => `${row.ring_time || 0}s`
    },
    {
      id: 'talk_time',
      label: 'Talk',
      headerCellProps: { title: 'Talk Time' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('talk_time'),
      renderCell: (row) => formatMinutes((row.talk_time || 0) / 60)
    },
    {
      id: 'handle_time',
      label: 'Handle',
      headerCellProps: { title: 'Handle Time (talk + after-call work)' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('handle_time'),
      renderCell: (row) => formatMinutes(row.handle_time / 60)
    },
    {
      id: 'call_notes',
      label: 'Note',
      headerCellProps: { title: 'Call Note' },
      sortable: true,
      cellClassName: styles.callNoteCell,
      style: { maxWidth: '250px' },
      renderSortIcon: () => renderSortIcon('call_notes'),
      renderCell: (row) => (
        <ExpandableText text={row.call_notes || ''} minWidth='200px' />
      )
    },
    {
      id: 'feedback',
      label: '',
      headerCellProps: { title: 'Feedback Rating' },
      sortable: true,
      cellClassName: styles.customTableCell,
      renderSortIcon: () => renderSortIcon('feedback'),
      align: 'center',
      renderCell: (row) => (
        <div className={styles.feedbackCell}>
          {row.vote === 'up' && (
            <FontAwesomeIcon icon='thumbs-up' className={styles.thumbsUp} />
          )}
          {row.vote === 'down' && (
            <FontAwesomeIcon icon='thumbs-down' className={styles.thumbsDown} />
          )}
          {!row.vote && '—'}
        </div>
      )
    }
  ]

  const renderHeaderContent = () => (
    <Box className={styles.resultsHeader}>
      {/* Results Header */}
      <Box className={styles.resultsHeaderContent}>
        <Typography className={styles.resultsTitle}>
          <FontAwesomeIcon icon={faList} className={styles.headerIcon} />
          {t('common.results')}
          {totalCalls > 0 && (
            <span className={styles.resultCount}>{totalCalls} {t('calls.title')}</span>
          )}
        </Typography>

        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
          {/* Reset Sorting Button - inline with header */}
          {searched && onResetSorting && (sortField !== 'call_date' || sortDirection !== 'desc') && (
            <Button
              variant='outlined'
              size='small'
              onClick={onResetSorting}
              className={styles.resetSortingButton}
              startIcon={<FontAwesomeIcon icon='undo' />}
            >
              {t('calls.table.resetSorting')}
            </Button>
          )}

          {/* Export CSV Button */}
          {searched && calls.length > 0 && onExportCsv && (
            <Button
              variant='outlined'
              size='small'
              onClick={onExportCsv}
              disabled={exporting}
              className={styles.exportButton}
              startIcon={exporting
                ? <CircularProgress size={14} />
                : <FontAwesomeIcon icon={faFileDownload} />}
            >
              {exporting ? t('calls.table.exporting') : t('calls.table.exportCsv')}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )

  const renderError = () => {
    if (error) {
      return (
        <Alert severity='error' className={styles.errorAlert}>
          {error}
        </Alert>
      )
    }
    return null
  }

  const renderEmptyState = () => {
    if (searched) {
      return (
        <TableEmptyState
          searched
          title='No Call Data Found'
          message='No calls match your current search criteria'
          icon='phone-slash'
        />
      )
    }

    return (
      <TableEmptyState
        searched={false}
        title={t('calls.callSearch')}
        message='Enter search criteria above to find calls'
        icon='search'
      />
    )
  }

  const handlePageChange = (event, value) => {
    if (tableTopRef.current) {
      tableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    onPageChange(event, value)
  }

  return (
    <>
      {/* Scroll anchor point */}
      <div ref={tableTopRef} style={{ position: 'relative', top: '-100px' }} />

      {renderError()}
      <ResponsiveTable
        columns={columns}
        data={calls}
        loading={loading}
        searched={searched}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        onRowClick={onRowClick}
        variant='calls'
        colSpan={columns.length}
        emptyState={renderEmptyState()}
        renderHeaderContent={renderHeaderContent}
        containerProps={{
          className: `${styles.tableContainer} ${styles.tableContainerCells} ${searched && calls.length > 0 ? styles.tableContainerWithPagination : ''}`
        }}
        tableProps={{
          'aria-label': 'calls table'
        }}
        page={searched && calls.length > 0 ? page : undefined}
        totalPages={searched && calls.length > 0 ? totalPages : undefined}
        onPageChange={searched && calls.length > 0 ? handlePageChange : undefined}
      />
    </>
  )
}

CallResultsTable.propTypes = {
  calls: PropTypes.array,
  loading: PropTypes.bool,
  searched: PropTypes.bool,
  error: PropTypes.string,
  page: PropTypes.number,
  totalPages: PropTypes.number,
  totalCalls: PropTypes.number,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  onPageChange: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  onResetSorting: PropTypes.func,
  onRowClick: PropTypes.func.isRequired,
  onExportCsv: PropTypes.func,
  exporting: PropTypes.bool
}

export default CallResultsTable
